import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SideDrawer — web implementation.
 *
 * Slides in from the left or right. Supports configurable widths,
 * swipe-to-close gesture on touch devices, and full accessibility.
 *
 * Design reference: FRE-372 SideDrawer specs
 * Default widths: mobile=80%, web=320px
 */
import { forwardRef, useEffect, useRef, useCallback, useId } from 'react';
import { X } from 'lucide-react';
import { Portal } from './Portal';
import { useFocusTrap } from './hooks/useFocusTrap';
import { useScrollLock } from './hooks/useScrollLock';
// ---------------------------------------------------------------------------
// Animation keyframes
// ---------------------------------------------------------------------------
const ANIM_ID = 'loci-drawer-keyframes';
function injectKeyframes() {
    if (typeof document === 'undefined' || document.getElementById(ANIM_ID))
        return;
    const style = document.createElement('style');
    style.id = ANIM_ID;
    style.textContent = `
    @keyframes fs-drawer-left-in  { from { transform: translateX(-100%); } to { transform: translateX(0); } }
    @keyframes fs-drawer-right-in { from { transform: translateX(100%);  } to { transform: translateX(0); } }
    @keyframes fs-backdrop-in     { from { opacity: 0; } to { opacity: 1; } }
  `;
    document.head.appendChild(style);
}
if (typeof window !== 'undefined') {
    injectKeyframes();
}
// ---------------------------------------------------------------------------
// SideDrawer
// ---------------------------------------------------------------------------
export const SideDrawer = forwardRef(({ open, onClose, direction = 'left', width = 320, closeOnBackdropClick = true, closeOnEscape = true, showCloseButton = true, header, children, footer, className, style, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, }, ref) => {
    const generatedId = useId();
    const labelId = ariaLabelledBy ?? generatedId;
    const { containerRef } = useFocusTrap({ active: open });
    useScrollLock(open);
    // Swipe-to-close: track touch start and end
    const touchStartXRef = useRef(0);
    const touchStartTimeRef = useRef(0);
    const handleTouchStart = useCallback((e) => {
        touchStartXRef.current = e.touches[0].clientX;
        touchStartTimeRef.current = performance.now();
    }, []);
    const handleTouchEnd = useCallback((e) => {
        const delta = e.changedTouches[0].clientX - touchStartXRef.current;
        const elapsed = (performance.now() - touchStartTimeRef.current) / 1000;
        const isSwipeLeft = delta < -50;
        const isSwipeRight = delta > 50;
        if (direction === 'left' && isSwipeLeft) {
            onClose();
        }
        else if (direction === 'right' && isSwipeRight) {
            onClose();
        }
    }, [direction, onClose]);
    // Escape key
    useEffect(() => {
        if (!open || !closeOnEscape)
            return;
        const handle = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', handle);
        return () => document.removeEventListener('keydown', handle);
    }, [open, closeOnEscape, onClose]);
    if (!open)
        return null;
    const resolvedWidth = typeof width === 'number' ? `${width}px` : width;
    const animationName = direction === 'left' ? 'fs-drawer-left-in' : 'fs-drawer-right-in';
    const positionStyle = direction === 'left' ? { left: 0, top: 0, bottom: 0 } : { right: 0, top: 0, bottom: 0 };
    return (_jsxs(Portal, { children: [_jsx("div", { style: {
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1000,
                    animation: 'fs-backdrop-in 200ms ease',
                }, onClick: closeOnBackdropClick ? onClose : undefined, "data-testid": "drawer-backdrop" }), _jsxs("div", { ref: (node) => {
                    containerRef.current = node;
                    if (typeof ref === 'function')
                        ref(node);
                    else if (ref)
                        ref.current = node;
                }, className: className, style: {
                    position: 'fixed',
                    ...positionStyle,
                    width: resolvedWidth,
                    maxWidth: '100vw',
                    backgroundColor: '#ffffff',
                    boxShadow: direction === 'left' ? '4px 0 20px rgba(0,0,0,0.12)' : '-4px 0 20px rgba(0,0,0,0.12)',
                    zIndex: 1001,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    animation: `${animationName} 300ms cubic-bezier(0.32,0.72,0,1)`,
                    outline: 'none',
                    ...style,
                }, tabIndex: -1, role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, "aria-labelledby": ariaLabel ? undefined : labelId, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, "data-testid": "drawer-panel", children: [(header ?? showCloseButton) && (_jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '20px 20px 0',
                            flexShrink: 0,
                            minHeight: 64,
                        }, children: [_jsx("div", { style: { flex: 1 }, children: header }), showCloseButton && (_jsx("button", { type: "button", onClick: onClose, "aria-label": "Close", style: {
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    lineHeight: 0,
                                    minWidth: 44,
                                    minHeight: 44,
                                    transition: 'color 150ms ease',
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.color = '#111827';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.color = '#6b7280';
                                }, onFocus: (e) => {
                                    e.currentTarget.style.outline = '2px solid #6c63ff';
                                    e.currentTarget.style.outlineOffset = '2px';
                                }, onBlur: (e) => {
                                    e.currentTarget.style.outline = 'none';
                                }, children: _jsx(X, { size: 20, strokeWidth: 2 }) }))] })), _jsx("div", { style: {
                            flex: 1,
                            overflowY: 'auto',
                            padding: '16px 20px',
                        }, children: children }), footer && (_jsx("div", { style: {
                            padding: '12px 20px 24px',
                            flexShrink: 0,
                            borderTop: '1px solid #f3f4f6',
                        }, children: footer }))] })] }));
});
SideDrawer.displayName = 'SideDrawer';
